import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";
import { CodeReveal } from "@/components/CodeReveal";
import { addToVault } from "@/lib/vault";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 12;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | done | error | timeout
  const [result, setResult] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (!sessionId) {
      setStatus("error");
      return;
    }

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const reveal = await axios.get(`${API}/payments/reveal/${sessionId}`);
          try {
            const pending = JSON.parse(sessionStorage.getItem("mlm_pending") || "{}");
            addToVault({
              code: reveal.data.code,
              title: reveal.data.title,
              recipient_name: reveal.data.recipient_name || pending.recipient_name || "",
              created_at: reveal.data.created_at,
            });
            sessionStorage.removeItem("mlm_pending");
          } catch (err) {
            console.warn("Could not save message to vault:", err);
          }
          setResult(reveal.data);
          setStatus("done");
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") {
          setStatus("error");
          return;
        }
      } catch (err) {
        console.warn("Payment status poll failed, retrying:", err);
      }
      if (attempts >= MAX_POLLS) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  if (status === "done" && result) {
    return <CodeReveal result={result} />;
  }

  return (
    <div className="min-h-screen bg-obsidian bg-grain">
      <Navbar />
      <div className="max-w-lg mx-auto px-5 sm:px-8 pt-40 pb-20 text-center">
        {status === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-10 h-10 text-gold mx-auto mb-6 animate-spin" />
            <h1 className="font-serif text-2xl sm:text-3xl text-gold-light">Confirming your payment…</h1>
            <p className="text-slate-400 mt-3">Please wait a moment while we seal your message.</p>
          </div>
        )}
        {(status === "error" || status === "timeout") && (
          <div data-testid="payment-error" className="glass-card rounded-2xl p-8">
            <AlertTriangle className="w-10 h-10 text-destructive mx-auto mb-5" />
            <h1 className="font-serif text-2xl text-gold-light">
              {status === "timeout" ? "Still processing…" : "We couldn't confirm your payment"}
            </h1>
            <p className="text-slate-400 mt-3">
              {status === "timeout"
                ? "Your payment is taking longer than expected. If you were charged, your message is safe — check your vault shortly."
                : "No worries — if you weren't charged, you can record and try again."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-7">
              <Button data-testid="go-vault-button" onClick={() => navigate("/vault")} variant="outline" className="rounded-full border-gold/30 text-slate-200 bg-transparent hover:bg-gold/10 hover:text-gold">
                Go to my vault
              </Button>
              <Button data-testid="go-record-button" onClick={() => navigate("/record")} className="rounded-full bg-gold hover:bg-gold-hover text-obsidian font-semibold">
                Record again
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
